import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type {
  AdminReviewListItem,
  AssessmentFixture,
  Page,
  Review,
  ReviewAuditEntry,
  Submission,
  Topic,
} from "@/types";

/**
 * React Query hooks for the admin Reviews feature. These wrap the existing API
 * client methods — the client is unchanged — and own the query keys, caching,
 * polling (via `refetchInterval`), and invalidation-on-success that previously
 * lived as manual `useEffect` loaders, a manual `loadData` + refetch, and a
 * hand-rolled `getReviewStatus` poller in the pages.
 *
 * The review detail query is keyed by `reviewId`, so changing the route param
 * changes the query key and React Query refetches naturally — replacing the
 * suppressed "reload only when reviewId changes" effect. Follows the Sources /
 * Assessment Fixtures slice templates.
 */

export interface ReviewsListParams {
  page: number;
  page_size: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  q?: string;
  status?: string;
  programme_id?: string;
  week_number?: number;
  assignment_number?: number;
}

export const reviewKeys = {
  all: ["admin", "reviews"] as const,
  lists: () => [...reviewKeys.all, "list"] as const,
  list: (params: ReviewsListParams) => [...reviewKeys.lists(), params] as const,
  detail: (reviewId: string) => [...reviewKeys.all, "detail", reviewId] as const,
  bySubmission: (submissionId: string) =>
    [...reviewKeys.all, "bySubmission", submissionId] as const,
  submission: (submissionId: string) => [...reviewKeys.all, "submission", submissionId] as const,
  submissionFileUrl: (submissionId: string) =>
    [...reviewKeys.all, "submissionFileUrl", submissionId] as const,
  topic: (topicId: string) => [...reviewKeys.all, "topic", topicId] as const,
  audit: (reviewId: string) => [...reviewKeys.all, "audit", reviewId] as const,
};

/** Admin reviews list, keyed by filters/page/sort. */
export function useAdminReviews(params: ReviewsListParams) {
  return useQuery<Page<AdminReviewListItem>>({
    queryKey: reviewKeys.list(params),
    queryFn: () => api.getAdminReviews(params),
  });
}

/**
 * Review detail, keyed by `reviewId`. While the review is in the `pending`
 * state it polls every 2s (replacing the manual `getReviewStatus` poller); it
 * stops once the AI pipeline finishes (`ai_reviewed`/`failed`) or settles into
 * any other status.
 */
export function useReview(reviewId: string | undefined) {
  return useQuery<Review>({
    queryKey: reviewKeys.detail(reviewId ?? ""),
    queryFn: () => api.getReview(reviewId as string),
    enabled: Boolean(reviewId),
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 2000 : false),
  });
}

/** Approval audit trail for a review, newest first. */
export function useReviewAudit(reviewId: string | undefined) {
  return useQuery<ReviewAuditEntry[]>({
    queryKey: reviewKeys.audit(reviewId ?? ""),
    queryFn: () => api.getReviewAudit(reviewId as string),
    enabled: Boolean(reviewId),
  });
}

/** A submission by id (current or previous revision). */
export function useSubmission(submissionId: string | undefined) {
  return useQuery<Submission>({
    queryKey: reviewKeys.submission(submissionId ?? ""),
    queryFn: () => api.getSubmission(submissionId as string),
    enabled: Boolean(submissionId),
  });
}

/** The review attached to a submission (used for the previous revision). */
export function useReviewBySubmission(submissionId: string | undefined) {
  return useQuery<Review | null>({
    queryKey: reviewKeys.bySubmission(submissionId ?? ""),
    queryFn: () => api.getReviewBySubmission(submissionId as string),
    enabled: Boolean(submissionId),
  });
}

/** Resolve the (auth'd) file URL for a submission's PDF. */
export function useSubmissionFileUrl(submissionId: string | undefined) {
  return useQuery<string>({
    queryKey: reviewKeys.submissionFileUrl(submissionId ?? ""),
    queryFn: () => api.getSubmissionFileUrl(submissionId as string),
    enabled: Boolean(submissionId),
  });
}

/** Topic context for the detail header. */
export function useReviewTopic(topicId: string | undefined) {
  return useQuery<Topic>({
    queryKey: reviewKeys.topic(topicId ?? ""),
    queryFn: () => api.getTopic(topicId as string),
    enabled: Boolean(topicId),
  });
}

/** Invalidate every Reviews query (lists, details, sub-resources) after a write. */
function useInvalidateReviews() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: reviewKeys.all });
}

export function useUpdateReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: ({ reviewId, data }: { reviewId: string; data: Partial<Review> }) =>
      api.updateReview(reviewId, data),
    onSuccess: invalidate,
  });
}

export function useSubmitForHumanReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: (reviewId: string) => api.submitForHumanReview(reviewId),
    onSuccess: invalidate,
  });
}

export function useCommitAdminReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: ({
      reviewId,
      data,
    }: {
      reviewId: string;
      data: {
        admin_sections: Record<string, unknown>;
        admin_final_score: number;
      };
    }) => api.commitAdminReview(reviewId, data),
    onSuccess: invalidate,
  });
}

export function useRetryReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: (reviewId: string) => api.retryReview(reviewId),
    onSuccess: invalidate,
  });
}

export function usePromoteReviewToFixture() {
  const invalidate = useInvalidateReviews();
  return useMutation<
    AssessmentFixture,
    Error & { status?: number; existingFixtureId?: string },
    { reviewId: string; name?: string | null; require_admin_score?: boolean }
  >({
    mutationFn: ({ reviewId, name, require_admin_score }) =>
      api.promoteReviewToFixture(reviewId, { name, require_admin_score }),
    onSuccess: invalidate,
  });
}
